/**
 * @fileoverview Shared factory for the local-first report and dashboard definition service,
 * used by both tenant-web (`reportDefinitionService`) and system-web (`systemReportDefinitionService`).
 * @module local-db/services/createReportDefinitionService
 *
 * Responsibilities: - Provide one DRY implementation of `report_definitions` CRUD, publish/archive,
 *                     duplicate, role-visibility filtering, tenant dashboard area assignment
 *                     (single-primary invariant, ordering), and system library visibility/exposure.
 *                   - `watchReportDefinitions`/`watchReportDefinition`: `liveQuery` subscriptions
 *                     whose observed function reads the RAW `report_definitions` table first —
 *                     pages must use these instead of `liveQuery(() => listReportDefinitions(...))`,
 *                     where the wrapped `getDb()` await escapes liveQuery's observation zone.
 *                   - Fix the `toggleLibrary` publish-readiness guard so dashboards
 *                     (`type === 'dashboard'`) check `dashboardConfig.widgets` instead of
 *                     `reportConfig.columns`/`dataSources`.
 * Dependencies:     - ../reporting/reportingTypes (ReportDefinitionLocal, SystemLibraryVisibility, etc.)
 *                   - ../replication/replicationFactory (wakeLocalWrite)
 *                   - ./createReportTemplateService (WriteAuditLogFn)
 * Architecture:     - Local-first: all reads and writes operate against the injected Dexie
 *                     `report_definitions` table first, then call `wakeLocalWrite('report_definitions')`.
 *                     No direct API calls.
 *                   - Audit logging is injected via `writeAuditLog` so `@firesquirrel/local-db`
 *                     does not depend on host audit services.
 * Notes:            - `sourceLevel` ('department' | 'system') controls scope-level guards on
 *                     `publishReportDefinition`, `archiveReportDefinition`, and `toggleLibrary`.
 */

import type {
  ReportDefinitionLocal,
  ReportType,
  ReportStatus,
  ReportScopeLevel,
  SystemLibraryVisibility,
} from '../reporting/reportingTypes';
import type Dexie from 'dexie';
import { wakeLocalWrite } from '../replication/replicationFactory';
import { watchDexieTable } from './watchDexieTable';
import type { WriteAuditLogFn } from './createReportTemplateService';

/**
 * @contract ReportDefinitionTable
 * Purpose: Minimal Dexie table surface required by `createReportDefinitionService`.
 * Fields:  toArray, get, put, patch
 * Rules:   patch is optional (falls back to put if absent on a mock or table).
 * Sync:    local-first Dexie table (`report_definitions`).
 */
export interface ReportDefinitionTable {
  toArray(): Promise<unknown[]>;
  get(id: string): Promise<unknown>;
  put(rec: Record<string, any>): Promise<unknown>;
  patch?(id: string, patch: Record<string, any>): Promise<unknown>;
}

/**
 * @contract ListReportDefinitionsParams
 * Purpose: Filter options for `listReportDefinitions` across tenant-web and system-web.
 * Fields:  type?, status?, includeDeleted?, tenantId?, includeSystem?, scopeLevel?
 * Rules:   All fields optional; soft-deleted records excluded unless `includeDeleted: true`.
 * Sync:    N/A (query filter).
 */
export interface ListReportDefinitionsParams {
  type?: ReportType;
  status?: ReportStatus;
  includeDeleted?: boolean;
  tenantId?: string;
  includeSystem?: boolean;
  scopeLevel?: 'system' | 'department';
}

/**
 * @contract ReportDefinitionWatchSubscription
 * Purpose: Handle returned by the reactive watch* service methods.
 * Fields:  unsubscribe (stop listening), ready (resolves once the liveQuery
 *          subscription is established — after the host DB is open).
 * Rules:   Callers subscribe immediately in their effect; `unsubscribe` is
 *          safe before `ready` resolves.
 * Sync:    backed by Dexie `liveQuery` on the raw `report_definitions` table.
 */
export interface ReportDefinitionWatchSubscription {
  unsubscribe(): void;
  ready: Promise<void>;
}

/**
 * @contract ReportDefinitionWatchHandlers
 * Purpose: Emission callbacks for watch* methods (Dexie `.subscribe` shape).
 * Fields:  next receives each filtered snapshot; error receives read failures.
 * Rules:   `next` fires once immediately after subscribe, then on every
 *          tracked `report_definitions` change.
 * Sync:    read-only; no writes happen inside the observed function.
 */
export interface ReportDefinitionWatchHandlers<T> {
  next: (items: T) => void;
  error?: (err: unknown) => void;
}

/**
 * @contract ReportDefinitionServiceDeps
 * Purpose: Injected dependencies that parameterize the shared report/dashboard definition service.
 * Fields:  getDb, sourceLevel, idPrefix?, copyNameSuffix?, auditCollection?, writeAuditLog?, getRaw?
 * Rules:   `getDb` must resolve to an object containing a `report_definitions` table.
 *          `getRaw` returns the underlying raw Dexie instance so the watch*
 *          methods can read the table synchronously inside `liveQuery`'s
 *          observation zone (required for change tracking; an awaited
 *          `getDb()` read escapes it).
 * Sync:    N/A (configuration).
 */
export interface ReportDefinitionServiceDeps {
  getDb: () => Promise<{ report_definitions: ReportDefinitionTable }>;
  /** Returns the raw Dexie handle backing `getDb`, or null until the DB is open. Required for watch* methods. */
  getRaw?: () => Dexie | null;
  sourceLevel: ReportScopeLevel;
  /** _id prefix for new/duplicated records. Default 'rdef'. */
  idPrefix?: string;
  /** Suffix appended to a duplicated definition's name. Default ' (copy)'. */
  copyNameSuffix?: string;
  /** Audit collection name ('reportDefinitions' for tenant, 'systemReportDefinitions' for system). */
  auditCollection?: string;
  /** Optional audit logger callback. */
  writeAuditLog?: WriteAuditLogFn;
}

/**
 * @contract ReportDefinitionService
 * Purpose: The local-first report and dashboard definition API returned by `createReportDefinitionService`.
 * Fields:  CRUD, lifecycle, role visibility, tenant dashboard assignment, and system library helpers.
 * Rules:   All reads are pure Dexie reads; all writes update Dexie and call `wakeLocalWrite('report_definitions')`.
 * Sync:    local-first; reconciled with MongoDB in the background.
 */
export interface ReportDefinitionService {
  listReportDefinitions(params?: ListReportDefinitionsParams): Promise<ReportDefinitionLocal[]>;
  getReportDefinition(id: string): Promise<ReportDefinitionLocal | null>;
  watchReportDefinitions(
    params: ListReportDefinitionsParams | undefined,
    handlers: ReportDefinitionWatchHandlers<ReportDefinitionLocal[]>,
  ): ReportDefinitionWatchSubscription;
  watchReportDefinition(
    id: string,
    handlers: ReportDefinitionWatchHandlers<ReportDefinitionLocal | null>,
  ): ReportDefinitionWatchSubscription;
  createReportDefinition(data: Partial<ReportDefinitionLocal>): Promise<ReportDefinitionLocal>;
  updateReportDefinition(id: string, patch: Partial<ReportDefinitionLocal>): Promise<ReportDefinitionLocal | null>;
  deleteReportDefinition(id: string): Promise<boolean>;
  publishReportDefinition(id: string): Promise<ReportDefinitionLocal | null>;
  archiveReportDefinition(id: string): Promise<ReportDefinitionLocal | null>;
  duplicateReportDefinition(id: string, createdBy?: string): Promise<ReportDefinitionLocal | null>;
  filterByRoleVisibility(
    items: ReportDefinitionLocal[],
    userRoleId: string | undefined,
    hasViewAllPermission: boolean,
  ): ReportDefinitionLocal[];
  updateVisibleToRoleIds(id: string, roleIds: string[]): Promise<ReportDefinitionLocal | null>;
  listAssignedDashboards(
    userRoleId?: string,
    hasViewAllPermission?: boolean,
  ): Promise<ReportDefinitionLocal[]>;
  getPrimaryDashboard(
    userRoleId?: string,
    hasViewAllPermission?: boolean,
  ): Promise<ReportDefinitionLocal | null>;
  setPrimaryDashboard(id: string): Promise<ReportDefinitionLocal | null>;
  clearPrimaryDashboard(id: string): Promise<ReportDefinitionLocal | null>;
  assignDashboardToArea(id: string, order?: number): Promise<ReportDefinitionLocal | null>;
  unassignDashboardFromArea(id: string): Promise<ReportDefinitionLocal | null>;
  reorderAssignedDashboards(orderedIds: string[]): Promise<void>;
  toggleLibrary(id: string, isInLibrary: boolean): Promise<ReportDefinitionLocal | null>;
  setReportDefinitionVisibility(
    id: string,
    visibility: SystemLibraryVisibility,
    allowedTenantIds?: string[],
  ): Promise<ReportDefinitionLocal | null>;
}

function nowIso(): string {
  return new Date().toISOString();
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Apply `ListReportDefinitionsParams` filters and the updatedAt-desc sort.
 * Pure helper — safe to call inside `liveQuery`'s observed function.
 */
function applyListFilters(
  docs: ReportDefinitionLocal[],
  params: ListReportDefinitionsParams = {},
): ReportDefinitionLocal[] {
  let results: ReportDefinitionLocal[] = docs;

  if (!params.includeDeleted) {
    results = results.filter((r) => !r._deleted);
  }
  if (params.type) {
    results = results.filter((r) => r.type === params.type);
  }
  if (params.status) {
    results = results.filter((r) => r.status === params.status);
  }
  if (params.tenantId) {
    results = results.filter((r) => r.scope?.tenantId === params.tenantId);
  }
  if (params.scopeLevel === 'system') {
    results = results.filter((r) => r.scope?.level === 'system');
  }
  if (params.scopeLevel === 'department') {
    results = results.filter((r) => r.scope?.level === 'department');
  }
  if (params.includeSystem === false) {
    results = results.filter((r) => Boolean(r.scope?.tenantId));
  }

  return results.sort((a, b) =>
    String(b.updatedAt ?? b.createdAt ?? '').localeCompare(String(a.updatedAt ?? a.createdAt ?? '')),
  );
}

/**
 * Subscribe a `liveQuery` to `read` once `deps.getDb()` resolves (host DB open,
 * so `deps.getRaw()` is non-null). `read` receives the raw
 * `report_definitions` table: fetching it inside the observed function — with
 * no non-Dexie await before the first table access — is what keeps the
 * subscription inside liveQuery's observation zone.
 */
function watchReportTable<T>(
  deps: ReportDefinitionServiceDeps,
  handlers: ReportDefinitionWatchHandlers<T>,
  read: (table: any) => Promise<T>,
  emptyValue: T,
): ReportDefinitionWatchSubscription {
  if (!deps.getRaw) {
    throw new Error(
      'createReportDefinitionService: getRaw dep is required for live subscriptions',
    );
  }
  const getRaw = deps.getRaw;
  return watchDexieTable(
    {
      open: () => deps.getDb(),
      getRaw,
      table: 'report_definitions',
      read,
      emptyValue,
    },
    handlers,
  );
}

/**
 * Filter report/dashboard definitions by role-based visibility.
 * Pure helper — safe to call inside `liveQuery`.
 *
 * @process filterByRoleVisibility
 * Steps:   1. If `hasViewAllPermission` is true, return all items.
 *          2. Otherwise keep items whose `visibleToRoleIds` is empty/absent or contains `userRoleId`.
 * Inputs:  items, userRoleId, hasViewAllPermission.
 * Outputs: Filtered ReportDefinitionLocal[].
 * Failure Handling: Pure synchronous filter; never throws.
 * Idempotency: REQUIRED — pure function.
 */
export function filterByRoleVisibility(
  items: ReportDefinitionLocal[],
  userRoleId: string | undefined,
  hasViewAllPermission: boolean,
): ReportDefinitionLocal[] {
  if (hasViewAllPermission) return items;
  return items.filter((item) => {
    if (!item.visibleToRoleIds || item.visibleToRoleIds.length === 0) return true;
    if (!userRoleId) return false;
    return item.visibleToRoleIds.includes(userRoleId);
  });
}

/**
 * @process createReportDefinitionService
 * Steps:   Build a local-first report/dashboard definition service bound to `deps.getDb` and `deps.sourceLevel`.
 * Inputs:  ReportDefinitionServiceDeps.
 * Outputs: ReportDefinitionService.
 * Failure Handling: Missing records return `null` (or `false` on delete); audit failures are caught and logged.
 * Idempotency: Assignment and visibility helpers are idempotent; CRUD increments version.
 * @localFirst
 * Source of Truth: local (Dexie `report_definitions`)
 * Write Flow: Dexie write → wakeLocalWrite('report_definitions') → background sync push
 * Sync Strategy: Optimistic local write; background replication
 * Conflict Resolution: Last-write-wins via `updatedAt`
 */
export function createReportDefinitionService(
  deps: ReportDefinitionServiceDeps,
): ReportDefinitionService {
  const { getDb, sourceLevel } = deps;
  const idPrefix = deps.idPrefix ?? 'rdef';
  const copyNameSuffix = deps.copyNameSuffix ?? ' (copy)';
  const auditCollection =
    deps.auditCollection ?? (sourceLevel === 'system' ? 'systemReportDefinitions' : 'reportDefinitions');
  const auditLog = deps.writeAuditLog;

  const SKIP = new Set([
    '_id', '_rev', '_replicator', '_syncSeq',
    'createdAt', 'updatedAt', 'createdBy', 'updatedBy',
    'deletedAt', 'deletedBy', 'version',
  ]);

  function diffFields(
    before: Record<string, unknown>,
    after: Record<string, unknown>,
  ): Array<{ field: string; oldValue?: unknown; newValue?: unknown; fieldType?: string }> {
    const changes: Array<{ field: string; oldValue?: unknown; newValue?: unknown; fieldType?: string }> = [];
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const k of keys) {
      if (SKIP.has(k)) continue;
      const o = before[k];
      const n = after[k];
      if (JSON.stringify(o) !== JSON.stringify(n)) {
        const val = n !== undefined ? n : o;
        const fieldType =
          val === null || val === undefined ? 'null' : Array.isArray(val) ? 'array' : typeof val;
        changes.push({ field: k, oldValue: o ?? null, newValue: n ?? null, fieldType });
      }
    }
    return changes;
  }

  async function safeAudit(opts: Parameters<WriteAuditLogFn>[0]): Promise<void> {
    if (!auditLog) return;
    try {
      await auditLog(opts);
    } catch (e) {
      console.warn('[audit] safeAudit failed:', e);
    }
  }

  /**
   * @localFirst Source of Truth: local (Dexie `report_definitions`). Pure read.
   */
  async function listReportDefinitions(
    params: ListReportDefinitionsParams = {},
  ): Promise<ReportDefinitionLocal[]> {
    const db = await getDb();
    const docs = await db.report_definitions.toArray();
    return applyListFilters(docs as ReportDefinitionLocal[], params);
  }

  /**
   * @localFirst Source of Truth: local (Dexie `report_definitions`). Pure read.
   */
  async function getReportDefinition(id: string): Promise<ReportDefinitionLocal | null> {
    const db = await getDb();
    return ((await db.report_definitions.get(id)) as ReportDefinitionLocal | undefined) ?? null;
  }

  /**
   * @process watchReportDefinitions
   * Steps:   1. Wait for `getDb()` so the host DB is open and `getRaw()` is non-null.
   *          2. Subscribe a `liveQuery` whose FIRST call is the raw
   *          `report_definitions` table read — no non-Dexie await precedes it —
   *          so Dexie registers the object store as a tracked dependency.
   *          3. Emit `applyListFilters` snapshots on every change until
   *          `unsubscribe()`.
   * Inputs:  ListReportDefinitionsParams + handlers.
   * Outputs: ReportDefinitionWatchSubscription (unsubscribe now, `ready` for the
   *          subscription-establishing signal).
   * Failure Handling: an absent `getRaw` dep throws immediately; a still-closed
   *          raw handle defers to the next emission; read errors go to
   *          `handlers.error`.
   * Idempotency: REQUIRED — subscribing twice creates two independent
   *          subscriptions, never a duplicate push.
   * @localFirst Source of Truth: local (Dexie `report_definitions`). Read-only.
   */
  function watchReportDefinitions(
    params: ListReportDefinitionsParams | undefined,
    handlers: ReportDefinitionWatchHandlers<ReportDefinitionLocal[]>,
  ): ReportDefinitionWatchSubscription {
    if (!deps.getRaw) {
      throw new Error('createReportDefinitionService: getRaw dep required for watchReportDefinitions');
    }
    return watchReportTable(
      deps,
      handlers,
      async (table) =>
        applyListFilters((await table.toArray()) as ReportDefinitionLocal[], params),
      [],
    );
  }

  /**
   * @process watchReportDefinition — same tracked-read contract as
   *   watchReportDefinitions, narrowed to one `_id`. Use this in place of
   *   `liveQuery(() => getReportDefinition(id))`: the wrapped `getDb()` await
   *   escapes liveQuery's observation zone, so that pattern never re-emits.
   * @localFirst Source of Truth: local (Dexie `report_definitions`). Read-only.
   */
  function watchReportDefinition(
    id: string,
    handlers: ReportDefinitionWatchHandlers<ReportDefinitionLocal | null>,
  ): ReportDefinitionWatchSubscription {
    if (!deps.getRaw) {
      throw new Error('createReportDefinitionService: getRaw dep required for watchReportDefinition');
    }
    return watchReportTable(
      deps,
      handlers,
      async (table) =>
        ((await table.get(id)) as ReportDefinitionLocal | undefined) ?? null,
      null,
    );
  }

  /**
   * @process createReportDefinition — Dexie mutation + wakeLocalWrite + audit entry.
   * @localFirst Source of Truth: local (Dexie). Write Flow: put → wakeLocalWrite → audit.
   */
  async function createReportDefinition(
    data: Partial<ReportDefinitionLocal>,
  ): Promise<ReportDefinitionLocal> {
    const db = await getDb();
    const now = nowIso();
    const record: ReportDefinitionLocal = {
      name: data.name ?? 'Untitled',
      type: data.type ?? 'report',
      status: data.status ?? 'draft',
      scope: data.scope ?? { level: sourceLevel },
      sharing: data.sharing ?? { sharedWith: [], isReadOnly: false },
      createdBy: data.createdBy ?? 'system',
      updatedBy: data.updatedBy ?? data.createdBy ?? 'system',
      ...data,
      _id: data._id || newId(idPrefix),
      version: 1,
      _deleted: false,
      createdAt: now,
      updatedAt: now,
    };

    await db.report_definitions.put(record as unknown as Record<string, any>);
    wakeLocalWrite('report_definitions');
    await safeAudit({
      collection: auditCollection,
      documentId: record._id,
      action: 'create',
      summary: `Created report definition: ${record.name}`,
    });
    return record;
  }

  /**
   * @process updateReportDefinition — Dexie mutation + wakeLocalWrite + audit entry.
   * @localFirst Source of Truth: local (Dexie). Write Flow: patch/put → wakeLocalWrite → audit.
   */
  async function updateReportDefinition(
    id: string,
    patch: Partial<ReportDefinitionLocal>,
  ): Promise<ReportDefinitionLocal | null> {
    const db = await getDb();
    const current = ((await db.report_definitions.get(id)) as ReportDefinitionLocal | undefined) ?? null;
    if (!current) return null;

    const updated: ReportDefinitionLocal = {
      ...current,
      ...patch,
      _id: id,
      version: (current.version ?? 1) + 1,
      updatedAt: nowIso(),
    };

    if (typeof db.report_definitions.patch === 'function') {
      await db.report_definitions.patch(id, updated as unknown as Record<string, any>);
    } else {
      await db.report_definitions.put(updated as unknown as Record<string, any>);
    }
    wakeLocalWrite('report_definitions');

    let changes: Array<{ field: string; oldValue?: unknown; newValue?: unknown; fieldType?: string }> | undefined;
    try {
      changes = diffFields(
        current as unknown as Record<string, unknown>,
        updated as unknown as Record<string, unknown>,
      );
    } catch {
      // diff failure must not block mutation
    }

    await safeAudit({
      collection: auditCollection,
      documentId: id,
      action: 'update',
      summary: `Updated report definition: ${current.name ?? id}`,
      changes,
    });
    return updated;
  }

  /**
   * @process deleteReportDefinition — Soft-delete tombstone (`_deleted: true`) + wakeLocalWrite + audit.
   * @localFirst Source of Truth: local (Dexie). Soft-delete via `_deleted: true` tombstone.
   */
  async function deleteReportDefinition(id: string): Promise<boolean> {
    const db = await getDb();
    const existing = ((await db.report_definitions.get(id)) as ReportDefinitionLocal | undefined) ?? null;
    if (!existing) return false;

    const now = nowIso();
    const tombstonePatch = { _deleted: true, deletedAt: now, updatedAt: now };
    if (typeof db.report_definitions.patch === 'function') {
      await db.report_definitions.patch(id, tombstonePatch);
    } else {
      await db.report_definitions.put({
        ...(existing as unknown as Record<string, any>),
        ...tombstonePatch,
      });
    }
    wakeLocalWrite('report_definitions');

    await safeAudit({
      collection: auditCollection,
      documentId: id,
      action: 'delete',
      summary: `Deleted report definition: ${existing.name ?? id}`,
      changes: [
        {
          field: '_deleted',
          oldValue: existing._deleted ?? false,
          newValue: true,
          fieldType: 'boolean',
        },
      ],
    });
    return true;
  }

  /**
   * @process publishReportDefinition — Sets status to 'published'.
   * @localFirst Source of Truth: local (Dexie).
   */
  async function publishReportDefinition(id: string): Promise<ReportDefinitionLocal | null> {
    if (sourceLevel === 'system') {
      const existing = await getReportDefinition(id);
      if (!existing || existing.scope?.level !== 'system') return null;
    }
    return updateReportDefinition(id, { status: 'published' });
  }

  /**
   * @process archiveReportDefinition — Sets status to 'archived' (and clears `isInLibrary` on system scope).
   * @localFirst Source of Truth: local (Dexie).
   */
  async function archiveReportDefinition(id: string): Promise<ReportDefinitionLocal | null> {
    if (sourceLevel === 'system') {
      const existing = await getReportDefinition(id);
      if (!existing || existing.scope?.level !== 'system') return null;
      return updateReportDefinition(id, { status: 'archived', isInLibrary: false });
    }
    return updateReportDefinition(id, { status: 'archived' });
  }

  /**
   * @process duplicateReportDefinition — Creates a draft copy with `derivedFrom` lineage and resets library/primary flags.
   * @localFirst Source of Truth: local (Dexie).
   */
  async function duplicateReportDefinition(
    id: string,
    createdBy?: string,
  ): Promise<ReportDefinitionLocal | null> {
    const db = await getDb();
    const src = ((await db.report_definitions.get(id)) as ReportDefinitionLocal | undefined) ?? null;
    if (!src) return null;

    const now = nowIso();
    const actor = createdBy ?? src.createdBy ?? 'system';
    const copy: ReportDefinitionLocal = {
      ...src,
      _id: newId(idPrefix),
      name: `${src.name}${copyNameSuffix}`,
      status: 'draft',
      isInLibrary: false,
      isDefault: false,
      assignmentOrder: undefined,
      version: 1,
      createdBy: actor,
      createdAt: now,
      updatedBy: actor,
      updatedAt: now,
      _deleted: false,
      deletedAt: undefined,
      deletedBy: undefined,
      derivedFrom: {
        reportDefinitionId: src._id,
        sourceLevel: src.scope?.level ?? sourceLevel,
        sourceId: src.scope?.tenantId ?? src.scope?.organizationId,
        copiedAt: now,
        copiedBy: actor,
      },
    };

    await db.report_definitions.put(copy as unknown as Record<string, any>);
    wakeLocalWrite('report_definitions');
    await safeAudit({
      collection: auditCollection,
      documentId: copy._id,
      action: 'create',
      summary: `Duplicated report definition from ${src.name}`,
    });
    return copy;
  }

  /**
   * @process updateVisibleToRoleIds — Updates role-based visibility list on a report/dashboard.
   * @localFirst Source of Truth: local (Dexie).
   */
  async function updateVisibleToRoleIds(
    id: string,
    roleIds: string[],
  ): Promise<ReportDefinitionLocal | null> {
    return updateReportDefinition(id, { visibleToRoleIds: roleIds.length > 0 ? roleIds : undefined });
  }

  /**
   * List dashboards assigned to the tenant-web Dashboard area, role-visibility filtered.
   * Assigned = type='dashboard' AND status='published' AND isInLibrary=true AND !_deleted.
   * Sorted by assignmentOrder (asc, undefined last) then updatedAt (desc).
   *
   * @process listAssignedDashboards
   * Steps:   1. Read all published dashboard definitions from Dexie.
   *          2. Filter `isInLibrary === true`.
   *          3. Apply role-visibility filtering (`filterByRoleVisibility`).
   *          4. Sort by `assignmentOrder` (asc, undefined last), then `updatedAt` (desc).
   * Inputs:  userRoleId, hasViewAllPermission (default true when omitted).
   * Outputs: ReportDefinitionLocal[] (assigned dashboards, ordered).
   * Failure Handling: Dexie read errors propagate to caller.
   * Idempotency: REQUIRED — pure read, no side effects.
   * @localFirst Source of Truth: local (Dexie). No API call.
   */
  async function listAssignedDashboards(
    userRoleId?: string,
    hasViewAllPermission: boolean = true,
  ): Promise<ReportDefinitionLocal[]> {
    const all = await listReportDefinitions({ type: 'dashboard', status: 'published' });
    const assigned = all.filter((r) => r.isInLibrary === true);
    const visible = filterByRoleVisibility(assigned, userRoleId, hasViewAllPermission);
    return visible.sort((a, b) => {
      const ao = a.assignmentOrder ?? Number.MAX_SAFE_INTEGER;
      const bo = b.assignmentOrder ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return String(b.updatedAt ?? '').localeCompare(String(a.updatedAt ?? ''));
    });
  }

  /**
   * Return the primary assigned dashboard (`isDefault=true` AND `isInLibrary=true` AND
   * `status='published'` AND `!_deleted`). If multiple match (race), the most recent by
   * `updatedAt` wins. Respects role visibility when `userRoleId` / `hasViewAllPermission`
   * are provided. If none match, returns `null`.
   *
   * @process getPrimaryDashboard
   * Steps:   1. Read published dashboards from Dexie.
   *          2. Filter `isDefault === true && isInLibrary === true`.
   *          3. Apply `filterByRoleVisibility` when role arguments are supplied.
   *          4. Sort by `updatedAt` desc and return first (or `null`).
   * Inputs:  userRoleId?, hasViewAllPermission? (defaults to true when omitted).
   * Outputs: ReportDefinitionLocal | null.
   * Failure Handling: Pure read; returns null if none match.
   * Idempotency: REQUIRED — pure read.
   * @localFirst Source of Truth: local (Dexie).
   */
  async function getPrimaryDashboard(
    userRoleId?: string,
    hasViewAllPermission: boolean = true,
  ): Promise<ReportDefinitionLocal | null> {
    const all = await listReportDefinitions({ type: 'dashboard', status: 'published' });
    const primaries = all.filter((r) => r.isDefault === true && r.isInLibrary === true);
    const visible = filterByRoleVisibility(primaries, userRoleId, hasViewAllPermission);
    if (visible.length === 0) return null;
    return visible.sort((a, b) => String(b.updatedAt ?? '').localeCompare(String(a.updatedAt ?? '')))[0];
  }

  /**
   * Set one dashboard as the primary (loads first on `/dashboard`). Clears
   * `isDefault` on all other dashboards in the tenant and sets `isDefault: true`
   * (and `isInLibrary: true` if not already set) on the target.
   *
   * @process setPrimaryDashboard
   * Steps:   1. Find all other non-deleted dashboards with `isDefault === true`.
   *          2. Clear `isDefault` on each via `updateReportDefinition`.
   *          3. Set `isDefault: true` on `id` (or no-op if already `isDefault: true`).
   * Inputs:  id — the dashboard to make primary.
   * Outputs: Updated ReportDefinitionLocal, or null if not found.
   * Failure Handling: Race invariant enforced by `getPrimaryDashboard` (most-recent `updatedAt` wins).
   * Idempotency: REQUIRED — setting the same dashboard primary twice is idempotent.
   * @localFirst Source of Truth: local (Dexie). Write Flow: Dexie → wakeLocalWrite → sync.
   * @security Validation: checks record existence. Trust Boundary: caller must hold `dashboards.edit` / `dashboardTemplates.edit`.
   */
  async function setPrimaryDashboard(id: string): Promise<ReportDefinitionLocal | null> {
    const db = await getDb();
    const target = ((await db.report_definitions.get(id)) as ReportDefinitionLocal | undefined) ?? null;
    if (!target || target._deleted) return null;

    const all = (await db.report_definitions.toArray()) as ReportDefinitionLocal[];
    const others = all.filter(
      (r) => r.type === 'dashboard' && r._id !== id && r.isDefault === true && !r._deleted,
    );
    for (const other of others) {
      await updateReportDefinition(other._id, { isDefault: false });
    }
    if (target.isDefault === true && others.length === 0) {
      return target;
    }
    return updateReportDefinition(id, { isDefault: true });
  }

  /**
   * Clear the primary flag on a dashboard WITHOUT unassigning it from the
   * tenant-web Dashboard area (`isInLibrary` stays `true`). With no primary set,
   * `/dashboard` falls back to the built-in overview per the loader contract.
   *
   * @process clearPrimaryDashboard
   * Steps:   Update `isDefault: false` on `id`.
   * Inputs:  id.
   * Outputs: Updated ReportDefinitionLocal | null.
   * Failure Handling: Returns null if dashboard not found.
   * Idempotency: REQUIRED.
   * @localFirst Source of Truth: local (Dexie).
   */
  async function clearPrimaryDashboard(id: string): Promise<ReportDefinitionLocal | null> {
    return updateReportDefinition(id, { isDefault: false });
  }

  /**
   * Assign a dashboard to the tenant-web Dashboard area (sets `isInLibrary: true`).
   * Optionally sets its `assignmentOrder`.
   *
   * @process assignDashboardToArea
   * Steps:   1. Build patch `{ isInLibrary: true, ...(order !== undefined ? { assignmentOrder: order } : {}) }`.
   *          2. Persist via `updateReportDefinition`.
   * Inputs:  id, order?.
   * Outputs: Updated ReportDefinitionLocal | null.
   * Failure Handling: Returns null if dashboard not found.
   * Idempotency: REQUIRED.
   * @localFirst Source of Truth: local (Dexie).
   */
  async function assignDashboardToArea(
    id: string,
    order?: number,
  ): Promise<ReportDefinitionLocal | null> {
    const patch: Partial<ReportDefinitionLocal> = { isInLibrary: true };
    if (typeof order === 'number') patch.assignmentOrder = order;
    return updateReportDefinition(id, patch);
  }

  /**
   * Remove a dashboard from the tenant-web Dashboard area (`isInLibrary: false`)
   * and clear `isDefault` if it was primary.
   *
   * @process unassignDashboardFromArea
   * Steps:   Update `isInLibrary: false, isDefault: false` on `id`.
   * Inputs:  id.
   * Outputs: Updated ReportDefinitionLocal | null.
   * Failure Handling: Returns null if dashboard not found.
   * Idempotency: REQUIRED.
   * @localFirst Source of Truth: local (Dexie).
   */
  async function unassignDashboardFromArea(id: string): Promise<ReportDefinitionLocal | null> {
    return updateReportDefinition(id, { isInLibrary: false, isDefault: false });
  }

  /**
   * Reorder the assigned dashboards by writing 0-indexed `assignmentOrder` for each id.
   *
   * @process reorderAssignedDashboards
   * Steps:   For each `(index, id)` in `orderedIds`, call `updateReportDefinition(id, { assignmentOrder: index })`.
   * Inputs:  orderedIds — ordered array of dashboard IDs.
   * Outputs: void.
   * Failure Handling: Missing IDs return null and are skipped without aborting remaining updates.
   * Idempotency: REQUIRED.
   * @localFirst Source of Truth: local (Dexie).
   */
  async function reorderAssignedDashboards(orderedIds: string[]): Promise<void> {
    for (let i = 0; i < orderedIds.length; i++) {
      await updateReportDefinition(orderedIds[i], { assignmentOrder: i });
    }
  }

  /**
   * Toggle System Library exposure (`isInLibrary`) for a system-scoped report or dashboard.
   * Fixes G-49: checks `dashboardConfig.widgets` when `existing.type === 'dashboard'`,
   * and `reportConfig.columns` / `reportConfig.dataSources` when `existing.type === 'report'`.
   *
   * @process toggleLibrary
   * Steps:   1. Verify record exists, `scope.level === 'system'`, and `status === 'published'`.
   *          2. When enabling (`isInLibrary === true`), verify readiness (at least 1 widget for
   *             dashboards; at least 1 column or dataSource for reports).
   *          3. Update `isInLibrary` and default `visibility` to `'all-departments'` if unset.
   * Inputs:  id, isInLibrary.
   * Outputs: Updated ReportDefinitionLocal | null.
   * Failure Handling: Returns null and logs a warning if readiness guard fails.
   * Idempotency: REQUIRED.
   * @localFirst Source of Truth: local (Dexie).
   */
  async function toggleLibrary(
    id: string,
    isInLibrary: boolean,
  ): Promise<ReportDefinitionLocal | null> {
    const existing = await getReportDefinition(id);
    if (!existing || existing.scope?.level !== 'system' || existing.status !== 'published') {
      return null;
    }

    if (isInLibrary) {
      if (existing.type === 'dashboard') {
        const hasWidgets = (existing.dashboardConfig?.widgets?.length ?? 0) > 0;
        if (!hasWidgets) {
          console.warn(
            `[toggleLibrary] Dashboard ${id} cannot be added to library: must have at least 1 widget`,
          );
          return null;
        }
      } else {
        const config = existing.reportConfig;
        const hasColumns = (config?.columns?.length ?? 0) > 0;
        const hasDataSources = (config?.dataSources?.length ?? 0) > 0;
        if (!hasColumns && !hasDataSources) {
          console.warn(
            `[toggleLibrary] Report ${id} cannot be added to library: missing columns and data sources`,
          );
          return null;
        }
      }
    }

    return updateReportDefinition(id, {
      isInLibrary,
      visibility: isInLibrary ? (existing.visibility ?? 'all-departments') : existing.visibility,
    });
  }

  /**
   * Update the System Library visibility (`all-departments` | `specific-tenants` | `system-only`)
   * and optional `allowedTenantIds` on a system report/dashboard definition.
   *
   * @process setReportDefinitionVisibility
   * Steps:   Update `visibility` and `allowedTenantIds` (cleared when visibility !== 'specific-tenants').
   * Inputs:  id, visibility, allowedTenantIds?.
   * Outputs: Updated ReportDefinitionLocal | null.
   * Failure Handling: Returns null if record not found.
   * Idempotency: REQUIRED.
   * @localFirst Source of Truth: local (Dexie).
   */
  async function setReportDefinitionVisibility(
    id: string,
    visibility: SystemLibraryVisibility,
    allowedTenantIds?: string[],
  ): Promise<ReportDefinitionLocal | null> {
    return updateReportDefinition(id, {
      visibility,
      allowedTenantIds: visibility === 'specific-tenants' ? (allowedTenantIds ?? []) : undefined,
    });
  }

  return {
    listReportDefinitions,
    getReportDefinition,
    watchReportDefinitions,
    watchReportDefinition,
    createReportDefinition,
    updateReportDefinition,
    deleteReportDefinition,
    publishReportDefinition,
    archiveReportDefinition,
    duplicateReportDefinition,
    filterByRoleVisibility,
    updateVisibleToRoleIds,
    listAssignedDashboards,
    getPrimaryDashboard,
    setPrimaryDashboard,
    clearPrimaryDashboard,
    assignDashboardToArea,
    unassignDashboardFromArea,
    reorderAssignedDashboards,
    toggleLibrary,
    setReportDefinitionVisibility,
  };
}
